import {
  InputField as InputFieldConfig,
  SelectField,
  RadioTableField as RadioTableConfig,
  ArrayField as ArrayFieldConfig,
  Field,
  TextAreaField as TextAreaConfig,
  AttachmentFieldConfig,
  RadioFieldConfig,
  SliderFieldConfig,
  PlaceFieldConfig,
  ToggleFieldConfig,
  SubjectFieldConfig,
  ValueRangeConfig,
  RadioGroupFieldConfig,
} from "./schema"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"
import {
  useController,
  useFieldArray,
  UseFieldArrayUpdate,
  useFormContext,
  UseFormRegisterReturn,
  useWatch,
} from "react-hook-form"
import {
  Combobox,
  ControlledCombobox,
  ControlledMultiCombobox,
} from "@/components/ui/combobox"
import { RadioGroupTable } from "@/components/ui/radiogroup-table"
import { PropertyRenderer } from "./renderer"
import { Button } from "@/components/ui/button"
import { CircleMinus } from "lucide-react"
import { useFormRenderer } from "./hooks"
import { getRenderer } from "./form-registry"
import { Textarea } from "@/components/ui/textarea"
import { useFileDataUrl } from "@/components/hooks/use-file-data-url"
import React, { useState } from "react"
import {
  ControlledRadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { ControlledSlider } from "@/components/ui/slider"
import { useFieldRenderNotify } from "../subscriptions/field-render-subscriber"
import { Trans } from "react-i18next"
import { useTObject, useTOptions } from "@/i18n"
import { ControlledPlaceInput } from "@/components/ui/place-input"
import { Toggle } from "@/components/ui/toggle"
import { ErrorMessage } from "@/components/ui/error-messages"
import { ErrorHint } from "@/components/ui/errorhint"

export function FieldRenderer(config: Exclude<Field, ValueRangeConfig>) {
  const { tFunc, registry } = useFormRenderer()
  const renderer = getRenderer(registry, config)
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const { label, name, tooltipContent, tooltipLink, ...rendererConfig } = config

  useFieldRenderNotify(name)

  const props = {
    ...register(name),
    className: "space-y-2",
    label: tFunc(label),
    errors,
    ...(tooltipContent && {
      tooltipContent: (
        <Trans
          t={tFunc}
          i18nKey={tooltipContent}
          components={{
            a: (
              <a
                href={tooltipLink}
                className="hover:text-accent-foreground underline"
              />
            ),
            b: <b />,
          }}
        />
      ),
    }),
  }

  return (
    <FormField {...props}>
      {renderer({ label, name, ...rendererConfig } as Exclude<
        Field,
        ValueRangeConfig
      >)}
    </FormField>
  )
}

export function InputField({ inputType }: InputFieldConfig) {
  return <Input type={inputType} />
}

InputField.type = "field:input" as const

export function ComboboxField({ useOptions, comboboxType }: SelectField) {
  const options = useOptions()
  const Component =
    comboboxType === "multi" ? ControlledMultiCombobox : ControlledCombobox

  return <Component options={options} />
}

ComboboxField.type = "field:combobox" as const

export function RadioTableField({
  useOptions,
  useRows,
  rowTooltipsArgs,
}: RadioTableConfig) {
  const options = useOptions()
  const rows = useRows()

  if (rowTooltipsArgs) {
    return (
      <RadioGroupTableWithTooltips
        rows={rows}
        options={options}
        rowTooltipsArgs={rowTooltipsArgs}
      />
    )
  }

  return <RadioGroupTable rows={rows} options={options} />
}
function RadioGroupTableWithTooltips({
  rows,
  options,
  rowTooltipsArgs,
}: Required<Pick<RadioTableConfig, "rowTooltipsArgs">> &
  Pick<React.ComponentProps<typeof RadioGroupTable>, "rows" | "options">) {
  const rowTooltips = useTObject(...rowTooltipsArgs)
  return (
    <RadioGroupTable rows={rows} options={options} rowTooltips={rowTooltips} />
  )
}

RadioTableField.type = "field:radioTable" as const

export function RadioField({ useOptions, name }: RadioFieldConfig) {
  const options = useOptions()
  const { control } = useFormContext()

  return (
    <ControlledRadioGroup
      name={name}
      control={control}
      className="relative flex flex-row"
    >
      {options.map((option) => (
        <FormField
          name={`${name}.${option.label}`}
          control={control}
          key={option.value}
          label={option.label}
          className="flex items-center space-x-2"
        >
          <RadioGroupItem value={option.value} id={`${name}.${option.label}`} />
        </FormField>
      ))}
    </ControlledRadioGroup>
  )
}
RadioField.type = "field:radio" as const

export function RadioGroupField({ useOptions, name }: RadioGroupFieldConfig) {
  const options = useOptions()
  const { control } = useFormContext()

  return (
    <ControlledRadioGroup
      name={name}
      control={control}
      className="grid w-full grid-cols-2 gap-2"
    >
      {options.map((option) => {
        const id = `${name}.${option.value}`
        return (
          <div key={id}>
            <RadioGroupItem
              value={option.value}
              id={id}
              className="peer sr-only"
              aria-labelledby={`${id}-label`}
              aria-describedby={`${id}-description`}
            />
            <Label
              htmlFor={id}
              className="border-muted peer-data-[state=checked]:border-primary peer-data-[state=checked]:text-primary peer-data-[state=checked]:bg-primary/10 peer-data-[state=checked]:hover:bg-primary/15 peer-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex h-full w-full cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors"
            >
              <option.icon
                className="mt-0.5 size-6 shrink-0"
                aria-hidden="true"
              />
              <div className="flex flex-col">
                <span className="font-medium" id={`${id}-label`}>
                  {option.label}
                </span>
                <span className="text-xs" id={`${id}-description`}>
                  {option.description}
                </span>
              </div>
            </Label>
          </div>
        )
      })}
    </ControlledRadioGroup>
  )
}
RadioGroupField.type = "field:radioGroup" as const

export function ArrayField({
  name,
  label,
  properties,
  defaultValue,
  addLabel,
  removeLabel,
  withAttachment,
}: ArrayFieldConfig) {
  const { tFunc } = useFormRenderer()
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const arrayFields = useFieldArray({ control, name })
  const attachmentFields = useFieldArray({
    control,
    name: withAttachment?.name ?? "",
  })

  const fields = withAttachment
    ? arrayFields.fields.map((field, index) => ({
        ...field,
        ...attachmentFields.fields[index],
      }))
    : arrayFields.fields

  const append = withAttachment
    ? () => {
        arrayFields.append(defaultValue)
        attachmentFields.append(withAttachment.defaultValue)
      }
    : arrayFields.append

  const remove = withAttachment
    ? (index: number) => {
        arrayFields.remove(index)
        attachmentFields.remove(index)
      }
    : arrayFields.remove

  useFieldRenderNotify(name)

  return (
    <>
      {fields.map((field, index) => (
        <div key={field.id} className="space-y-4 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <h3 id={field.id} className="font-medium">
              {tFunc(label, {
                index: index + 1,
              })}
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(index)}
              aria-label={tFunc(removeLabel, { index: index + 1 })}
            >
              <CircleMinus className="h-4 w-4" />
            </Button>
          </div>
          <fieldset aria-labelledby={field.id}>
            {properties.map((property) =>
              withAttachment ? (
                <ArrayFieldWithAttachmentProperty
                  key={property.name}
                  name={name}
                  index={index}
                  property={property}
                  attachmentFieldName={withAttachment.name}
                  update={attachmentFields.update}
                />
              ) : (
                <ArrayFieldProperty
                  key={property.name}
                  name={name}
                  index={index}
                  property={property}
                />
              ),
            )}
          </fieldset>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={() => append(defaultValue)}
      >
        {tFunc(addLabel)}
      </Button>
      {errors && (
        <ErrorMessage
          namespaces={["common"]}
          name={name}
          errors={errors}
          render={ErrorHint}
        />
      )}
    </>
  )
}

ArrayField.type = "field:custom:array" as const

function ArrayFieldProperty({
  name,
  index,
  property,
}: {
  name: string
  index: number
  property: Field
}) {
  const updatedProperty = {
    ...property,
    name: `${name}.${index}.${property.name}`,
  }

  return (
    <PropertyRenderer
      key={updatedProperty.name}
      formProperty={updatedProperty}
    />
  )
}

function ArrayFieldWithAttachmentProperty({
  name,
  index,
  property,
  attachmentFieldName,
  update,
}: React.ComponentProps<typeof ArrayFieldProperty> & {
  attachmentFieldName: string
  update: UseFieldArrayUpdate<any>
}) {
  const { control } = useFormContext()
  const attachmentValues = useWatch({ control, name: attachmentFieldName })

  if (property.type !== "field:attachment") {
    return <ArrayFieldProperty name={name} index={index} property={property} />
  }

  const updatedProperty = {
    ...property,
    name: `${attachmentFieldName}.${index}.${property.name}`,
    useAttachment: () => attachmentValues[index],
    removeLabel:
      "common:actions.remove" as AttachmentFieldConfig["removeLabel"],
    onRemove: () => {
      update(index, {
        attached: false,
        signed_id: null,
      })
    },
  }

  return (
    <PropertyRenderer
      key={updatedProperty.name}
      formProperty={updatedProperty}
    />
  )
}

export function TextAreaField(_props: TextAreaConfig) {
  return <Textarea />
}

TextAreaField.type = "field:textarea" as const

interface FileInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  previewSrc?: string
}
const FileInput = ({
  ref,
  previewSrc,
  onRemove,
  removeLabel,
  ...props
}: FileInputProps &
  UseFormRegisterReturn & {
    ref: React.Ref<HTMLInputElement>
    removeLabel?: AttachmentFieldConfig["removeLabel"]
    onRemove?: AttachmentFieldConfig["onRemove"]
  }) => {
  const { tFunc } = useFormRenderer()
  return (
    <>
      <Input type="file" ref={ref} {...props} />
      {previewSrc && <img src={previewSrc} />}
      {previewSrc !== undefined && onRemove && removeLabel ? (
        <Button type="button" variant="destructive-outline" onClick={onRemove}>
          <CircleMinus />
          {tFunc(removeLabel)}
        </Button>
      ) : null}
    </>
  )
}

FileInput.displayName = "FileInput"

export function AttachmentField({
  name,
  accept,
  useAttachment,
  type,
  ...rest
}: AttachmentFieldConfig & UseFormRegisterReturn) {
  const { watch } = useFormContext()
  const previewDataUrl = useFileDataUrl(watch(name)?.item(0) ?? null)
  const attachment = useAttachment?.()

  if (!attachment) return <></>

  const previewSrc =
    previewDataUrl || (attachment.attached ? attachment.url : undefined)

  return (
    <FileInput name={name} accept={accept} previewSrc={previewSrc} {...rest} />
  )
}

AttachmentField.type = "field:attachment" as const

const BoundLabelledSlider = ({
  ref,
  min,
  max,
  upperBoundsLabel,
  lowerBoundsLabel,
  ...props
}: React.ComponentPropsWithRef<typeof ControlledSlider> &
  UseFormRegisterReturn & {
    upperBoundsLabel?: string
    lowerBoundsLabel?: string
    labelId?: string
  }) => (
  <>
    <ControlledSlider min={min} max={max} ref={ref} {...props} />
    {upperBoundsLabel && lowerBoundsLabel && (
      <div className="flex flex-row justify-between">
        <Label className="text-muted-foreground text-xs">
          {lowerBoundsLabel}
        </Label>
        <Label className="text-muted-foreground text-xs">
          {upperBoundsLabel}
        </Label>
      </div>
    )}
  </>
)
BoundLabelledSlider.displayName = "BoundLabelledSlider"

export function SliderField({
  upperBoundsLabel,
  lowerBoundsLabel,
  ...props
}: SliderFieldConfig & UseFormRegisterReturn) {
  const { tFunc } = useFormRenderer()
  return (
    <BoundLabelledSlider
      {...props}
      lowerBoundsLabel={lowerBoundsLabel ? tFunc(lowerBoundsLabel) : undefined}
      upperBoundsLabel={upperBoundsLabel ? tFunc(upperBoundsLabel) : undefined}
    />
  )
}

SliderField.type = "field:slider" as const

export function PlaceField({
  ...props
}: PlaceFieldConfig & UseFormRegisterReturn) {
  return <ControlledPlaceInput {...props} />
}
PlaceField.type = "field:place" as const

export function ToggleField({
  name,
  label,
  isArray,
  value,
}: ToggleFieldConfig) {
  const { tFunc } = useFormRenderer()
  const { control } = useFormContext()
  const { field } = useController({ name, control })
  useFieldRenderNotify(name)

  const pressed = isArray ? field.value.includes(value) : field.value === value
  const onPressedChange = (pressed: boolean) => {
    if (isArray) {
      if (pressed) {
        field.onChange([...field.value, value])
      } else {
        field.onChange(field.value.filter((v: any) => v !== value))
      }
    } else {
      field.onChange(pressed ? value : null)
    }
  }

  return (
    <Toggle
      variant={"outline"}
      pressed={pressed}
      onPressedChange={onPressedChange}
    >
      {tFunc(label)}
    </Toggle>
  )
}
ToggleField.type = "field:custom:toggle" as const

export function SubjectField({
  name,
  curriculaLabel,
  label,
}: SubjectFieldConfig) {
  const { tFunc } = useFormRenderer()
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()
  useFieldRenderNotify(name)

  const curricula = useTOptions("curricula", "subjects")
  const subjects = useTOptions("subjects", "subjects")

  const selectedSubjectKey = `${watch(name)}`
  const [selectedCurriculaKey, setSelectedCurriculaKey] = useState<
    string | null
  >(selectedSubjectKey.split(".").at(0) ?? null)
  const subjectsForSelectedCurriculum = subjects.filter(
    (subject) => subject.value.split(".")[0] === selectedCurriculaKey,
  )

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-4">
      <FormField
        name="curricula_id"
        control={control}
        className="space-y-2"
        label={tFunc(curriculaLabel)}
      >
        <Combobox
          onChange={(curriculaKey) => {
            setSelectedCurriculaKey(curriculaKey)
            setValue(name, null)
          }}
          options={curricula}
          value={selectedCurriculaKey}
        />
      </FormField>
      <FormField
        name={name}
        control={control}
        label={tFunc(label)}
        errors={errors}
        className="space-y-2"
      >
        <ControlledCombobox options={subjectsForSelectedCurriculum} />
      </FormField>
    </div>
  )
}
SubjectField.type = "field:custom:subject" as const

export function ValueRange({ name, properties }: ValueRangeConfig) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-4">
      {properties.map((config) => {
        const formProperty = { ...config, name: `${name}.${config.name}` }
        return (
          <PropertyRenderer
            key={formProperty.name}
            formProperty={formProperty}
          />
        )
      })}
    </div>
  )
}
ValueRange.type = "field:custom:value-range" as const
